<div class="flex flex-col justify-between h-full">
  @if (floating()) {
    @if (insights().length) {
      <lib-insights-panel>
        @for (item of insights(); track $index) {
          <lib-stat
            [label]="
              item.isMoney
                ? (item.value.toString() | money)
                : item.value.toString()
            "
            [overline]="item.label | transloco"
            [iconSvgName]="item.iconName || 'info'" />
        }
      </lib-insights-panel>
    }
    <section
      class="floating-card p-2xl flex flex-col"
      [class.mt-lg]="insights().length > 0">
      @if (showTableHeader()) {
        <ng-container [ngTemplateOutlet]="tableHeader" />
      }
      <ng-container [ngTemplateOutlet]="tableBody" />
    </section>
  } @else {
    @if (insights().length) {
      <div
        data-test="filtered-table-insights"
        class="mt-2xl w-full grid gap-lg"
        [class]="
          systemDefaultColor()
            ? '[grid-template-columns:repeat(auto-fill,minmax(14rem,1fr))]'
            : '[grid-template-columns:repeat(auto-fill,minmax(23rem,1fr))]'
        ">
        @for (item of insights(); track $index) {
          <div>
            <lib-stat
              [label]="
                item.isMoney
                  ? (item.value.toString() | money)
                  : item.value.toString()
              "
              [overline]="item.label | transloco"
              [iconSvgName]="item.iconName || 'info'" />
          </div>
        }
      </div>
    }
    @if (showTableHeader()) {
      <ng-container [ngTemplateOutlet]="tableHeader" />
    }
    <ng-container [ngTemplateOutlet]="tableBody" />
  }
</div>

<ng-template #tableHeader>
  <div
    class="flex items-center justify-between gap-lg min-w-0"
    [class.mt-2xl]="!hideTopSpacing() && !floating()"
    [class.pt-lg]="!hideTopSpacing() && !floating()">
    <div class="flex items-center gap-md min-w-0">
      @if (headerStartTemplate(); as startTemplate) {
        <ng-container [ngTemplateOutlet]="startTemplate" />
      } @else if (showTableTitle()) {
        <h5 class="text-neutral-900 font-semibold text-lg {{ titleClass() }}">
          {{ tableTitle() }}
        </h5>
        @if (totalItems() > 0) {
          <span
            class="rounded-full text-xs text-center font-medium px-sm bg-primary-100 text-primary-700"
            [style.minWidth]="'2rem'">
            {{ totalItems() }}
          </span>
        }
      }
    </div>

    <!-- Tables_new_design: the search moves into the table's toolbar row -->
    @if (showSearch() && !searchInToolbar()) {
      <div class="flex items-center gap-md shrink-0">
        <ng-container [ngTemplateOutlet]="searchField" />
      </div>
    }
  </div>
</ng-template>

<ng-template #searchField>
  <lib-search-field
    [searchInputWidth]="`${searchInputWidth}`"
    (pressedKeyType)="pressedKeyType.set($event)"
    [placeholder]="searchPlaceholder() || 'search_by_name_placeholder'"
    (searchKeywordEmitter)="handleSearchKeyword($event)" />
</ng-template>

<ng-template #tableBody>
  <!-- Floating: the table area fills the stretched card so the paginator sits at its bottom -->
  <!-- Without a header row, keep the table clear of the insights above it -->
  <div
    [class.flex-1]="floating()"
    [class.mt-2xl]="!floating() && insights().length > 0 && !showTableHeader()"
    data-filtered-table-body>
    @if (isTreeTable()) {
      @if (treeLabelTemplate(); as labelTemplate) {
        <lib-tree-table
          [id]="tableId()"
          [tableClass]="tableClass()"
          [dataSource]="treeDataSource()"
          [loading]="isLoadingData()"
          [columns]="columns()"
          [parentColumns]="parentColumns()"
          [labelTemplate]="labelTemplate"
          [cellTemplates]="treeCellTemplates()"
          [enableRowClick]="enableRowClick()"
          [viewCount]="meta()?.total || 0"
          [enableFilterRouting]="true"
          [tableFilterTabs]="tableFilterTabs()"
          [allowViewToggle]="allowViewToggle()"
          [isTableExportable]="isTableExportable()"
          [emptyStateTemplateRef]="emptyStateTemplateRef()"
          [tableActionSectionRef]="tableActionSectionRef()"
          [searchTemplateRef]="treeSearchTemplateRef()"
          [isCalendarViewEnabled]="isCalendarViewEnabled()"
          [calendarTemplateRef]="calendarTemplateRef()"
          [galleryCardTemplateRef]="galleryCardTemplateRef()"
          [galleryClass]="galleryClass()"
          [(isGalleryView)]="isGalleryView"
          [lastColumnAlignment]="lastColumnAlignment()"
          [filterDropdownTemplateRef]="
            filterOptions().length ? filterDropdown : null
          "
          [rowTestIdPrefix]="rowTestIdPrefix()"
          [toggleTestIdPrefix]="toggleTestIdPrefix()"
          [expandAriaLabel]="expandAriaLabel()"
          [collapseAriaLabel]="collapseAriaLabel()"
          (submitTableExport)="submitExportForm($event)"
          (handleSortColumn)="handleSortColumn($event)"
          (handleTableFilterTab)="handleTableFilterTab($event)"
          (handleViewIconClick)="handleViewIconClick.emit($event)"
          (toggleExpanded)="toggleExpanded.emit($event)"
          (handleRowClick)="handleRowClick.emit($event)" />
      }
    } @else {
      <lib-table
        [id]="tableId()"
        [showGridListTabs]="showGridListTabs()"
        [chipFiltersTemplateRef]="chipFilters().length ? chipFiltersRow : null"
        [tableClass]="tableClass()"
        [dataSource]="dataSource()"
        [loading]="isLoadingData()"
        [columns]="columns()"
        [parentColumns]="parentColumns()"
        [enableRowClick]="enableRowClick()"
        [viewCount]="meta()?.total || 0"
        [enableFilterRouting]="true"
        [noDataTitle]="'table_no_data_title' | transloco"
        [noDataDescription]="'table_no_data_description' | transloco"
        [tableFilterTabs]="tableFilterTabs()"
        [allowViewToggle]="allowViewToggle()"
        [isTableExportable]="isTableExportable()"
        [emptyStateTemplateRef]="emptyStateTemplateRef()"
        [tableActionSectionRef]="tableActionSectionRef()"
        [isCalendarViewEnabled]="isCalendarViewEnabled()"
        [calendarTemplateRef]="calendarTemplateRef()"
        [galleryCardTemplateRef]="galleryCardTemplateRef()"
        [galleryClass]="galleryClass()"
        [(isGalleryView)]="isGalleryView"
        (submitTableExport)="submitExportForm($event)"
        (handleSortColumn)="handleSortColumn($event)"
        (handleTableFilterTab)="handleTableFilterTab($event)"
        (handleViewIconClick)="handleViewIconClick.emit($event)"
        [bodyTabTemplateRef]="tableColumnTemplates()"
        [searchTemplateRef]="searchInToolbar() ? searchField : null"
        [lastColumnAlignment]="lastColumnAlignment()"
        [filterDropdownTemplateRef]="
          filterOptions().length ? filterDropdown : null
        ">
      </lib-table>
    }

    <ng-template #filterDropdown>
      <lib-filter-dropdown
        [filterOptions]="filterOptions()"
        [initialValues]="initialFilterValues()"
        (clearAll)="onClearAllFilters()"
        (applyFilters)="onApplyFilters($event)" />
    </ng-template>

    <ng-template #chipFiltersRow>
      <lib-chips-filter
        [filters]="chipFilters()"
        [value]="chipFilterValue()"
        (valueChange)="onChipFiltersChange($event)" />
    </ng-template>
  </div>

  @let metaData = meta();
  @if (metaData && dataSource().length > 0) {
    <lib-paginator
      class="mt-lg"
      [sizeOptionsLabel]="
        'pagination_number_of_rows_per_page_label' | transloco
      "
      [nextBtnLabel]="'pagination_next_button_label' | transloco"
      [previousBtnLabel]="'pagination_previous_button_label' | transloco"
      [currentPage]="metaData.current_page"
      [totalPages]="metaData.last_page"
      [showChangeSize]="true"
      [sizeOptions]="pageSizeOptions()"
      (goTo)="handlePagination($event)"
      (next)="handlePagination($event)"
      (previous)="handlePagination($event)"
      (handleChangeSize)="changeSize($event)"
      [currentSize]="metaData.per_page"
      [totalItems]="metaData.total"></lib-paginator>
  }
</ng-template>
